import React, { useState, useEffect } from 'react';
import { handleApiExceptions, generate_query_set, alertify } from '../../../app_methods';
import { ContentCard, UserModal, PermissionModal } from '../../Common';
import { EaseTable } from '../../../libs/EaseTable';
import { isMobile } from 'react-device-detect';
import { getUsersTableColumns, usersFilterNodes } from './constants';
import { User } from '../../../models';
import { Button, Icon, Alert, Descriptions } from 'antd';
import { UserDetailsDrawer } from './details_drawer';
import { connect } from 'react-redux';
import { updateCurrentUserAction } from '../../../redux/actions';
import { PermissionHoc } from '../../HOC';
import { ResendPasswordModal } from '../../Common/ResendPasswordModal';
import { Text4 } from '../../../styles';

const CreatePermissableButton = PermissionHoc(Button,"USERS_WRITE",{ is_component: true, fallbackComponent: null })


const UserList = (props) => {
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [pagination, setPagination] = useState({
        pageSize: 10,
        total: 0,
        current: 1,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
        showTotal: (total) => `Total ${total} Records`
    });
    const [filters, setFilters] = useState({});
    const [users, setUsers] = useState([]);
    const [masterUsers, setMasterUsers] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [selectedUser, setSelectedUser] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
    const [showResendPassword, setShowResendPassword] = useState(false);
    const [showResendPasswordModal, setShowResendPasswordModal] = useState(false);


    const fetchUsers = async (_pagination, _filters) => {
        if (!props.current_user.key) return;
        setIsTableLoading(true);
        const response = await User.list({ ...{ pageSize: _pagination.pageSize, current: _pagination.current }, ..._filters });
        if (!response.success) {
            setIsTableLoading(false);
            handleApiExceptions(response);
            return;
        }
        setUsers(generate_query_set(User, response.data.results));
        setPagination({ ...pagination, total: response.data.count });
        setIsTableLoading(false);
    };

    const fetchAllUsers = async () => {
        if (!props.current_user.key) return;
        setIsTableLoading(true);
        const response = await User.list();
        if (!response.success) {
            setIsTableLoading(false);
            handleApiExceptions(response);
            return;
        }
        setMasterUsers(generate_query_set(User, response.data.results));
        setIsTableLoading(false);
    };

    const fetchPermissions = async () => {
        if (!props.current_user.key) return;
        setIsTableLoading(true);
        const response = await User.permission_list();
        if (!response.success) {
            setIsTableLoading(false);
            handleApiExceptions(response);
            return;
        }
        setPermissions(response.data);
        setIsTableLoading(false);
    };

    const sendPassword = async (user = {}) => {
        setSelectedUser(user);
        setShowResendPassword(true);
        const response = await user.send_password();
        if (!response.data.success) {
                    setIsSaving(false);
                    handleApiExceptions("Something went wrong. Please try again.");
                    return;
        }
        alertify("Success", `Password reset link sent successfully.`, "success");
        setShowResendPassword(false);
    }

    const updateStatus = async (user = {}, is_active = false) => {
        setSelectedUser(user);
        setIsSaving(true);
        const response = await user.update_status({ active: is_active });
        if (!response.success) {
            setIsSaving(false);
            handleApiExceptions(response);
            return;
        }
        setIsSaving(false);
        fetchUsers(pagination, filters);
        alertify("Success", `Status updated successfully.`, "success");
    }

    const saveUser = async (values) => {
        setIsSaving(true);
        const user = new User(values);
        const response = await user.save();
        if (!response.success) {
            setIsSaving(false);
            handleApiExceptions(response);
            return;
        }
        toggleUserModal();
        alertify("Success", `User ${selectedUser.is_edit ? "updated" : "created"} successfully.`, "success");
        setIsSaving(false);
        fetchUsers(pagination, filters);
    };

    const setUserPermission = async (values) => {
        setIsSaving(true);
        const user = new User(values);
        const response = await user.setPermission();
        if (!response.success) {
            setIsSaving(false);
            handleApiExceptions(response);
            return;
        }
        togglePermissionModal();
        alertify("Success", `User Permissions updated successfully.`, "success");
        setIsSaving(false);
        fetchUsers(pagination, filters);
        fetchAllUsers();
    };

    const toggleUserModal = (user = {}) => {
        setSelectedUser(user);
        setShowUserModal(!showUserModal);
    };

    const togglePermissionModal = (user = {}) => {
        setSelectedUser(user);
        setShowPermissionModal(!showPermissionModal);
    };

    const toggleDetailsDrawer = (user = {}) => {
        setSelectedUser(user);
        setShowDetailsDrawer(!showDetailsDrawer);
    };

    const toggleResendPasswordModal = (user = {}) => {
        setSelectedUser(user);
        setShowResendPasswordModal(!showResendPasswordModal);
    };
    
    const onCaptchaVerify = () => {
        sendPassword(selectedUser)
        toggleResendPasswordModal({});
    }

    const handleTableChange = (tableInfo) => {
        if (tableInfo.trigger == "columns") return;
        let _pagination = tableInfo.pagination;
        if (tableInfo.trigger == "filters") _pagination.current = 1;
        setPagination(_pagination);
        setFilters(tableInfo.filters);
        fetchUsers(_pagination, tableInfo.filters);
    };

    const tableColumns = getUsersTableColumns({
        toggleUserModal,
        toggleDetailsDrawer,
        sendPassword,
        togglePermissionModal,
        updateStatus,
        toggleResendPasswordModal
    }, props.current_user);
    const tableFilters = usersFilterNodes;

    useEffect(() => {
        props.onChildMount();
        fetchUsers({ ...pagination }, { ...filters });
        fetchAllUsers();
        fetchPermissions();
    }, []);

    return (
        props.current_user.eum?<ContentCard className="p-3">
            <div className=" d-flex justify-content-between">
                <Descriptions column={1} bordered className='mr-2'>
                    <Descriptions.Item label={"Merchant Login URL"} className='w-100 d-flex justify-content-center'>
                        <Text4 copyable >{props?.current_user?.white_label_setting?.login_url ?? '---'}</Text4>
                    </Descriptions.Item>
                </Descriptions>
                <CreatePermissableButton type="primary" onClick={toggleUserModal}>
                    <Icon type="plus" /> New User
                </CreatePermissableButton>
            </div>
            <EaseTable
                rowKey="id"
                loading={isTableLoading}
                data={users}
                columns={tableColumns}
                fixedSelectable={!isMobile}
                handleTableChange={handleTableChange}
                pagination={pagination}
                totalRecords={pagination.total}
                filterNodes={tableFilters}
            />
            <UserModal
                visible={showUserModal}
                toggleUserModal={toggleUserModal}
                user={selectedUser}
                saveUser={saveUser}
                loading={isSaving}
            />
            <PermissionModal
                visible={showPermissionModal}
                togglePermissionModal={togglePermissionModal}
                user={selectedUser}
                users={users}
                cloneUsers={masterUsers}
                permissions={permissions}
                setUserPermission={setUserPermission}
                loading={isSaving}
                is_reseller={props.current_user.is_reseller}
            />
            <UserDetailsDrawer
                visible={showDetailsDrawer}
                toggleDetailsDrawer={toggleDetailsDrawer}
                user={selectedUser}
            />
            <ResendPasswordModal 
                visible={showResendPasswordModal}
                merchant={selectedUser}
                onCaptchaVerify={onCaptchaVerify}
                toggleResendPasswordModal={toggleResendPasswordModal}
            />
        </ContentCard>:
        <Alert
      message="Error"
      description="You are not authorised to view this page."
      type="error"
      showIcon
    />



    )
};

const mapStateToProps = state => ({
    current_user: state.userReducer.current_user
});

const mapDispatchToProps = dispatch => ({
    updateCurrentUser: user => dispatch(updateCurrentUserAction(user))
});

export default connect(mapStateToProps, mapDispatchToProps)(PermissionHoc(UserList, 'USERS_READ'));